import { api } from "@/lib/api";
import { PaginatedReq } from "../types/paginated.type";
import { Context, ContextPack, Dropzone } from "./types/context.type";
import ENV from "@/utils/env";
import { UseCase } from "@/api/manage/categories/types/usecase.type";

/* Request Types */
export interface GetAllContextPacksReq extends PaginatedReq {
  query?: string;
  sortBy?: "asc" | "desc";
  orderBy?: string;
  view?: "all" | "draft" | "archived" | "published";
  useCaseIds?: string[];
  userIds?: string[];
  isDragonfly?: boolean | null;
}

export interface CreateContextPackReq {
  name: string;
  useCaseId: string;
  useCaseName: string;
}

export interface UpdateContextPackReq {
  name?: string;
  isArchived?: boolean;
  isPublished?: boolean;
}

export interface UpdateContextReq {
  name?: string;
  dropzone?: Dropzone;
}

export interface AddContextToPackReq {
  packId: string;
  contextId: string;
  name: string;
  dropzone?: Dropzone;
}

/* Response Types */
export interface GetAllContextPacksResp {
  packs: Array<ContextPack>;
  pageIndex: number;
  maxPageIndex: number;
  totalItems: number;
  useCases: UseCase[];
  users: Array<{
    id: string;
    username: string;
  }>;
}

export interface CreateContextPackResp {
  pack: ContextPack;
}

export interface UpdateContextPackResp {
  pack: ContextPack;
}

export interface UpdateContextResp {
  context: Context;
}

export interface GetContextPackResp {
  pack: ContextPack;
}

/*
 * getAllContextPacks endpoint
 *
 * Returns a list of context packs with optional query parameters
 * Multiple use cases and users are passed as an array of strings and added as query parameters
 * e.g. useCaseIds=<useCaseId>&useCaseIds=<useCaseId2>
 */
export const getAllContextPacks = async (params: GetAllContextPacksReq) => {
  const resp = await api.get(`${ENV.API_PLATFORM_URL}/v3/contexts/packs`, {
    params,
    paramsSerializer: (params) =>
      new URLSearchParams(Object.entries(params).flatMap(([key, value]) => (Array.isArray(value) ? value.map((v) => [key, v]) : [[key, value]]))).toString(),
  });

  return resp.data as GetAllContextPacksResp;
};

/*
 * getContextPack endpoint
 *
 * Returns a single context pack by id
 */
export const getContextPack = async (contextPackId: string) => {
  const resp = await api.get(`${ENV.API_PLATFORM_URL}/v3/contexts/packs/${contextPackId}`);
  return resp.data as GetContextPackResp;
};

/*
 * createContextPack endpoint
 *
 * Creates a new context pack
 */
export const createContextPack = async (values: CreateContextPackReq) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/contexts/packs`, values);
  return resp.data as CreateContextPackResp;
};

/*
 * updateContextPack endpoint
 *
 * Updates a context pack
 */
export const updateContextPack = async (contextPackId: string, values: UpdateContextPackReq) => {
  const resp = await api.put(`${ENV.API_PLATFORM_URL}/v3/contexts/packs/${contextPackId}`, values);
  return resp.data as UpdateContextPackResp;
};

/*
 * updateContext endpoint
 *
 * Updates a single context
 */
export const updateContext = async (contextId: string, values: UpdateContextReq) => {
  const resp = await api.put(`${ENV.API_PLATFORM_URL}/v3/contexts/${contextId}`, values);
  return resp.data as UpdateContextResp;
};

/*
 * deleteContextPack endpoint
 *
 * Deletes a context pack
 */
export const deleteContextPack = async (contextPackId: string) => {
  const resp = await api.delete(`${ENV.API_PLATFORM_URL}/v3/contexts/packs/${contextPackId}`);
  return resp.data;
};

/*
 * addContextToPack endpoint
 *
 * Adds a context to a context pack
 */
export const addContextToPack = async (values: AddContextToPackReq) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/contexts`, values);
  return resp.data;
};
